import React from 'react';
import { createRoot, Root } from 'react-dom/client';

/**
 * Wraps a React component as an Angular directive factory.
 * Use this instead of the `react2angular` npm package — that package uses the
 * removed ReactDOM.render() and breaks with React 18+.
 *
 * Usage in directives/react/index.ts:
 *   frontendApp.directive('reactMyPage', react2angular(MyPage, ['prop1']));
 *
 * Usage in Angular template / route:
 *   <react-my-page prop1="ctrl.value"></react-my-page>
 *
 * @param Component  React component to mount
 * @param propNames  Angular scope bindings to forward as React props (one-way `<`)
 */
export function react2angular<P extends object>(
  Component: React.ComponentType<P>,
  propNames: (keyof P)[] = []
) {
  const scopeBindings = propNames.reduce(
    (acc, name) => ({ ...acc, [name as string]: '<' }),
    {} as Record<string, string>
  );

  return () => ({
    restrict: 'E',
    scope: scopeBindings,
    link(scope: any, element: any) {
      const el: HTMLElement = element[0];
      let root: Root | null = createRoot(el);

      function render() {
        const props = propNames.reduce(
          (acc, name) => ({ ...acc, [name as string]: scope[name as string] }),
          {} as P
        );
        root!.render(React.createElement(Component, props));
      }

      render();

      propNames.forEach((name) => {
        scope.$watch(name as string, render);
      });

      scope.$on('$destroy', () => {
        root!.unmount();
        root = null;
      });
    },
  });
}
